import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Save, SlidersHorizontal, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { AuthContext } from "../App";
import RetencionesConfigSection from "../components/RetencionesConfigSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inp = "w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500/60";

// Cada sección agrupa configuraciones globales. Para mudar una configuración
// nueva acá: sumarla en backend/routes/config_global.py (_DEFAULTS/_VALIDADORES)
// y agregar su campo en una sección.
function Seccion({ icono: Icono, titulo, descripcion, children }) {
  return (
    <section className="bg-arandu-dark-light border border-white/10 rounded-2xl p-5">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
          <Icono className="w-5 h-5 text-blue-400" />
        </div>
        <div>
          <h2 className="text-white font-heading text-base">{titulo}</h2>
          {descripcion && <p className="text-slate-400 text-xs mt-0.5">{descripcion}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}

export default function ConfigGlobalPage() {
  const { token, hasPermission } = useContext(AuthContext);
  const esAdmin = hasPermission("configuracion_global.editar");
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${API}/admin/config-global`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(setCfg).catch(() => toast.error("No se pudo cargar la configuración"));
  }, [token]);

  const guardar = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/config-global`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(cfg),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "No se pudo guardar");
      setCfg(data);
      toast.success("Configuración global guardada. Se aplica a todos los usuarios al volver a iniciar sesión o recargar.");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const set = (k, v) => setCfg(prev => ({ ...prev, [k]: v }));

  return (
    <div className="min-h-screen bg-arandu-dark text-white">
      <header className="border-b border-white/10 px-6 py-4 flex items-center gap-3">
        <Link to="/sistema" className="text-slate-400 hover:text-white"><ArrowLeft className="w-5 h-5" /></Link>
        <SlidersHorizontal className="w-5 h-5 text-blue-400" />
        <div>
          <h1 className="font-heading text-lg">Configuración global</h1>
          <p className="text-slate-400 text-xs">Ajustes que afectan a todo el sistema, no a una empresa en particular.</p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-6 space-y-5">
        {!cfg ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : (
          <>
            {!esAdmin && (
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 text-amber-200 text-xs px-4 py-3">
                No tenés permiso para modificar estos valores (solo lectura).
              </div>
            )}

            <Seccion icono={Clock} titulo="Sesión e inactividad"
              descripcion="Cierre automático de la sesión cuando no hay actividad.">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-slate-400 text-xs mb-1 block">Cerrar sesión tras (minutos)</label>
                  <input type="number" min="5" max="1440" disabled={!esAdmin} className={inp}
                    value={cfg.inactividad_minutos} onChange={e => set("inactividad_minutos", e.target.value)} />
                  <p className="text-slate-500 text-[11px] mt-1">Entre 5 y 1440 (24 h). Actual: {Math.round(cfg.inactividad_minutos / 60 * 10) / 10} h.</p>
                </div>
                <div>
                  <label className="text-slate-400 text-xs mb-1 block">Avisar antes de cerrar (minutos)</label>
                  <input type="number" min="1" max="60" disabled={!esAdmin} className={inp}
                    value={cfg.aviso_inactividad_minutos} onChange={e => set("aviso_inactividad_minutos", e.target.value)} />
                </div>
              </div>
            </Seccion>

            {hasPermission("retenciones.ver_config") && (
              <RetencionesConfigSection token={token} puedeEditar={hasPermission("retenciones.editar_config")} />
            )}

            <p className="text-slate-500 text-xs text-center">
              Acá se irán sumando las demás configuraciones globales a medida que las vayamos mudando.
            </p>

            {esAdmin && (
              <div className="flex justify-end">
                <button onClick={guardar} disabled={saving}
                  className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white text-sm flex items-center gap-2">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar
                </button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
